import React, { useEffect, useState } from 'react';
import {
  AI_REVIEW_SEVERITY_LABEL,
  AI_REVIEW_VERDICT_LABEL,
  type AiReviewFinding,
  type AiReviewPublishEvent,
  type AiReviewSeverity,
} from '@zunelo/shared';
import type { GitHubAiReviewState } from '../../context/slices/useGitHubSlice';
import type { AiReviewProgress } from '../../domain/aiReviewProgress';
import { Badge, Button } from '../../ui/common';

export interface GitHubAiReviewPanelProps {
  itemId: string;
  state: GitHubAiReviewState;
  /** Authors can only comment on their own PR (GitHub rejects approve/request changes). */
  isAuthor: boolean;
  onRegenerate: () => void;
  onPublish: (event: AiReviewPublishEvent) => void;
  onDismiss: () => void;
  onRetryStatus?: () => void;
}

const SEVERITY_TONE: Record<AiReviewSeverity, 'bad' | 'warn' | 'accent' | 'flat'> = {
  critical: 'bad',
  high: 'bad',
  medium: 'warn',
  low: 'flat',
};

const EVENT_LABEL: Record<AiReviewPublishEvent, string> = {
  COMMENT: 'Comentar',
  APPROVE: 'Aprobar',
  REQUEST_CHANGES: 'Pedir cambios',
};

function FindingRow({ finding }: { finding: AiReviewFinding }): React.ReactElement {
  const location = finding.line ? `${finding.path}:${finding.line}` : finding.path;
  return (
    <li
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '8px 0',
        borderTop: '1px solid var(--z-border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Badge
          label={AI_REVIEW_SEVERITY_LABEL[finding.severity]}
          tone={SEVERITY_TONE[finding.severity]}
        />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--z-text-2)' }}>
          {finding.title}
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            color: 'var(--z-dim)',
          }}
        >
          {location}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--z-muted-3)' }}>
        {finding.detail}
      </p>
      {finding.suggestion ? (
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--z-accent-copy)' }}>
          Sugerencia: {finding.suggestion}
        </p>
      ) : null}
    </li>
  );
}

function ReviewProgress({
  itemId,
  progress,
}: {
  itemId: string;
  progress?: AiReviewProgress;
}): React.ReactElement {
  const hasMetadata = Boolean(
    progress &&
    (progress.completedBatches !== undefined ||
      progress.totalBatches !== undefined ||
      progress.findingCount !== undefined ||
      progress.attempt !== undefined),
  );
  return (
    <div
      data-testid={`github-ai-review-progress-${itemId}`}
      style={{ fontSize: 12.5, color: 'var(--z-muted-3)' }}
    >
      <p style={{ margin: 0 }}>{progress?.message ?? 'Preparing the review…'}</p>
      {hasMetadata ? (
        <small style={{ display: 'block', marginTop: 4, color: 'var(--z-dim)' }}>
          {progress?.completedBatches !== undefined || progress?.totalBatches !== undefined
            ? `Batches: ${progress.completedBatches ?? 0}/${progress.totalBatches ?? '?'}`
            : null}
          {progress?.findingCount !== undefined
            ? ` · Findings collected: ${progress.findingCount}`
            : null}
          {progress?.attempt !== undefined ? ` · Attempt: ${progress.attempt}` : null}
        </small>
      ) : null}
      {progress?.updatedAt ? (
        <small style={{ display: 'block', marginTop: 4, color: 'var(--z-dim)' }}>
          Last update: {progress.updatedAt}
        </small>
      ) : null}
    </div>
  );
}

function PublishControls({
  itemId,
  state,
  isAuthor,
  onPublish,
}: Pick<
  GitHubAiReviewPanelProps,
  'itemId' | 'state' | 'isAuthor' | 'onPublish'
>): React.ReactElement | null {
  const review = state.review;
  // Always start at COMMENT: the model's verdict is steerable by the PR author,
  // so an attesting APPROVE must be an explicit, confirmed human choice.
  const [event, setEvent] = useState<AiReviewPublishEvent>('COMMENT');
  const [approveArmed, setApproveArmed] = useState(false);
  const [resetNotice, setResetNotice] = useState(false);
  const reviewKey = review ? `${review.headSha}:${review.generatedAt}` : '';
  useEffect(() => {
    // A regenerated review may carry a different verdict: tell the user their
    // previous choice was dropped instead of silently reverting to Comentar.
    setEvent((prev) => {
      if (prev !== 'COMMENT') setResetNotice(true);
      return 'COMMENT';
    });
    setApproveArmed(false);
  }, [reviewKey]);
  if (!review) return null;
  if (state.status === 'published' && state.publishResult) {
    const result = state.publishResult;
    return (
      <div
        data-testid={`github-ai-review-published-${itemId}`}
        style={{ fontSize: 12, color: 'var(--z-ok-ink)' }}
      >
        ✓ Publicado en GitHub como {EVENT_LABEL[result.event]}
        {result.inlineComments ? ` · ${result.inlineComments} comentario(s) en línea` : ''}
        {result.reviewUrl ? (
          <>
            {' · '}
            <a href={result.reviewUrl} target="_blank" rel="noopener noreferrer">
              Ver review
            </a>
          </>
        ) : null}
      </div>
    );
  }
  const effectiveEvent: AiReviewPublishEvent = isAuthor ? 'COMMENT' : event;
  const needsConfirmation = effectiveEvent === 'APPROVE' && !approveArmed;
  const publish = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (needsConfirmation) {
      setApproveArmed(true);
      return;
    }
    onPublish(effectiveEvent);
  };
  const label =
    state.status === 'publishing'
      ? 'Publicando…'
      : needsConfirmation
        ? 'Aprobar en GitHub…'
        : effectiveEvent === 'APPROVE'
          ? 'Confirmar aprobación'
          : 'Publicar en GitHub';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <select
        aria-label="Tipo de review"
        data-testid={`github-ai-review-event-${itemId}`}
        value={effectiveEvent}
        disabled={isAuthor || state.status === 'publishing'}
        onChange={(e) => {
          setEvent(e.target.value as AiReviewPublishEvent);
          setApproveArmed(false);
          setResetNotice(false);
        }}
        style={{
          fontSize: 12,
          padding: '4px 8px',
          borderRadius: 8,
          border: '1px solid var(--z-border)',
          background: 'var(--z-surface)',
          color: 'var(--z-text)',
        }}
      >
        {(Object.keys(EVENT_LABEL) as AiReviewPublishEvent[]).map((value) => (
          <option key={value} value={value}>
            {EVENT_LABEL[value]}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        variant={effectiveEvent === 'APPROVE' && approveArmed ? 'bad' : 'primary'}
        data-testid={`github-ai-review-publish-${itemId}`}
        disabled={state.status === 'publishing' || state.status === 'loading'}
        onClick={publish}
      >
        {label}
      </Button>
      {isAuthor ? (
        <span style={{ fontSize: 11.5, color: 'var(--z-dim)' }}>
          En tu propio PR solo puedes comentar.
        </span>
      ) : null}
      {resetNotice && !isAuthor ? (
        <span
          data-testid={`github-ai-review-reset-notice-${itemId}`}
          style={{ fontSize: 11.5, color: 'var(--z-dim)' }}
        >
          Nueva revisión: el tipo de review volvió a Comentar.
        </span>
      ) : null}
      {effectiveEvent === 'APPROVE' && approveArmed ? (
        <span
          data-testid={`github-ai-review-approve-warning-${itemId}`}
          style={{ fontSize: 11.5, color: 'var(--z-danger, #b91c1c)' }}
        >
          Aprobar cuenta como tu aprobación real en GitHub. Revisa el diff antes de confirmar.
        </span>
      ) : null}
    </div>
  );
}

export const GitHubAiReviewPanel: React.FC<GitHubAiReviewPanelProps> = ({
  itemId,
  state,
  isAuthor,
  onRegenerate,
  onPublish,
  onDismiss,
  onRetryStatus,
}) => {
  const review = state.review;
  return (
    <section
      data-testid={`github-ai-review-${itemId}`}
      aria-label="Revisión con IA"
      onClick={(e) => e.stopPropagation()}
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        background: 'var(--z-accent-wash)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--z-accent-ink)' }}>
          ✦ Revisión con IA
        </span>
        {review ? (
          <Badge
            label={AI_REVIEW_VERDICT_LABEL[review.verdict]}
            tone={
              review.verdict === 'request_changes'
                ? 'bad'
                : review.verdict === 'approve'
                  ? 'ok'
                  : 'accent'
            }
          />
        ) : null}
        {review?.cached ? (
          <Badge
            label="Reutilizada"
            tone="flat"
            title="Mismo commit: se reutilizó la revisión anterior"
          />
        ) : null}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button
            size="sm"
            variant="ghost"
            data-testid={`github-ai-review-regenerate-${itemId}`}
            disabled={state.status === 'loading' || state.status === 'publishing'}
            onClick={onRegenerate}
          >
            {state.status === 'loading' ? 'Revisando…' : 'Regenerar'}
          </Button>
          <Button size="sm" variant="ghost" aria-label="Cerrar revisión" onClick={onDismiss}>
            ✕
          </Button>
        </span>
      </div>

      {state.status === 'loading' ? (
        <ReviewProgress itemId={itemId} progress={state.progress} />
      ) : null}

      {state.statusRefreshError ? (
        <div
          role="alert"
          data-testid={`github-ai-review-status-error-${itemId}`}
          style={{ fontSize: 12.5, color: 'var(--z-danger, #b91c1c)' }}
        >
          <span>{state.statusRefreshError.message}</span>{' '}
          {onRetryStatus ? (
            <Button size="sm" variant="ghost" onClick={onRetryStatus}>
              Retry status
            </Button>
          ) : null}
        </div>
      ) : null}

      {state.error ? (
        <div
          role="alert"
          data-testid={`github-ai-review-error-${itemId}`}
          style={{ fontSize: 12.5, color: 'var(--z-danger, #b91c1c)' }}
        >
          {state.error}
        </div>
      ) : null}

      {review ? (
        <>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--z-text-2)' }}>
            {review.summary}
          </p>
          {review.findings.length ? (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {review.findings.map((finding, idx) => (
                <FindingRow
                  key={`${finding.path}-${finding.line ?? idx}-${idx}`}
                  finding={finding}
                />
              ))}
            </ul>
          ) : (
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--z-muted-3)' }}>
              Sin hallazgos bloqueantes.
            </p>
          )}
          {review.testingNotes.length ? (
            <div style={{ fontSize: 12, color: 'var(--z-muted-3)' }}>
              <strong style={{ color: 'var(--z-text-2)' }}>Tests:</strong>{' '}
              {review.testingNotes.join(' · ')}
            </div>
          ) : null}
          <div style={{ fontSize: 11, color: 'var(--z-dim)' }}>
            Revisado en{' '}
            <code data-testid={`github-ai-review-sha-${itemId}`}>{review.headSha.slice(0, 7)}</code>{' '}
            · {review.filesReviewed} archivo(s) revisados
            {review.filesSkipped.length ? ` · ${review.filesSkipped.length} omitidos` : ''}
            {review.truncated ? ' · diff truncado' : ''}
            {review.model ? ` · ${review.model}` : ''}
          </div>
          <PublishControls
            itemId={itemId}
            state={state}
            isAuthor={isAuthor}
            onPublish={onPublish}
          />
        </>
      ) : null}
    </section>
  );
};
